/**
 * Expanded Question Bank: Domain 3 — Spatial Visualization & 3D Reasoning
 * 15 Stochastic Spatial Reasoning Templates with Crisp Scalable Vector Stimuli
 */

import { wrapSvg, renderOptionSvg, DrawShapes } from './svg-builder.js';

export const EXPANDED_SPATIAL_TEMPLATES = [
  // 1. Isometric Block Stack Count (with hidden blocks)
  {
    id: 'spatial_isometric_stack_count',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Configuration of column heights on a 3x3 base
      // [c0, c1, c2] in back row, [c3, c4, c5] middle, [c6, c7, c8] front
      const configs = [
        { heights: [3, 2, 1, 2, 2, 0, 1, 0, 0], total: 11 },
        { heights: [3, 3, 2, 2, 1, 1, 1, 0, 0], total: 13 },
        { heights: [2, 3, 3, 1, 2, 1, 0, 1, 1], total: 14 },
        { heights: [3, 1, 2, 2, 2, 1, 1, 1, 0], total: 12 },
        { heights: [4, 2, 1, 3, 1, 0, 1, 0, 0], total: 12 }
      ];
      const cfg = prng.pick(configs);
      const correctVal = cfg.total;

      const distSet = new Set();
      distSet.add(correctVal + 1);
      distSet.add(correctVal - 1);
      distSet.add(correctVal + 2);
      distSet.add(correctVal - 2);
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      // Render 3D isometric stack SVG
      // Base isometric projection: x_iso = (x - y) * cos(30), y_iso = (x + y) * sin(30) - z * h
      let cubesSvg = '';
      const w = 26;
      const h = 26;
      const ox = 190;
      const oy = 110;

      // Draw from back to front: row 0 to 2, col 0 to 2
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const idx = r * 3 + c;
          const height = cfg.heights[idx];
          for (let z = 0; z < height; z++) {
            const px = ox + (c - r) * (w * 0.866);
            const py = oy + (c + r) * (w * 0.5) - (z * h);

            // Isometric cube faces
            const top = `M ${px} ${py - h} L ${px + w*0.866} ${py - h*0.5} L ${px} ${py} L ${px - w*0.866} ${py - h*0.5} Z`;
            const left = `M ${px - w*0.866} ${py - h*0.5} L ${px} ${py} L ${px} ${py + h} L ${px - w*0.866} ${py + h*0.5} Z`;
            const right = `M ${px} ${py} L ${px + w*0.866} ${py - h*0.5} L ${px + w*0.866} ${py + h*0.5} L ${px} ${py + h} Z`;

            cubesSvg += `
              <path d="${top}" fill="#38bdf8" stroke="#0f172a" stroke-width="1.2"/>
              <path d="${left}" fill="#0284c7" stroke="#0f172a" stroke-width="1.2"/>
              <path d="${right}" fill="#0369a1" stroke="#0f172a" stroke-width="1.2"/>
            `;
          }
        }
      }

      const stimulusSvg = `<svg viewBox="0 0 380 200" xmlns="http://www.w3.org/2000/svg" style="max-height:190px; margin:0 auto; display:block;">
        <rect width="380" height="200" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        ${cubesSvg}
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val} مكعبات`,
        text_en: `${val} cubes`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `كم عدد المكعبات المتطابقة التي يتكون منها هذا المجسم ثلاثي الأبعاد بالكامل (مع احتساب المكعبات المخفية الداعمة)؟`,
        questionText_en: `How many identical unit cubes make up this entire 3D structure (including hidden supporting cubes)?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `بحساب أعمدة المكعبات من الخلف للأمام: مجموع الارتفاعات = ${cfg.heights.filter(h => h > 0).join(' + ')} = ${correctVal} مكعباً.`,
        explanation_en: `Summing column heights from back to front yields a total of ${correctVal} cubes.`
      };
    }
  },

  // 2. 3D Cross-Section Slicing
  {
    id: 'spatial_cone_cross_section',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const solids = [
        {
          name_ar: 'مخروط دائري قائم',
          name_en: 'Right circular cone',
          cut_ar: 'مستوى مائل يقطع جميع المولدات دون ملامسة القاعدة',
          cut_en: 'An inclined plane cutting through all generators without touching the base',
          res_ar: 'قطع ناقص (إهليلج / بيضاوي)',
          res_en: 'Ellipse',
          distractors_ar: ['دائرة تامة', 'مثلث متساوي الساقين', 'قطع مكافئ (بارابولا)'],
          distractors_en: ['Perfect circle', 'Isosceles triangle', 'Parabola']
        },
        {
          name_ar: 'أسطوانة دائرية قائمة',
          name_en: 'Right circular cylinder',
          cut_ar: 'مستوى رأسي موازٍ لمحور الأسطوانة تماماً يمر بمنتصفها',
          cut_en: 'A vertical plane parallel to the axis passing through its center',
          res_ar: 'مستطيل كامل',
          res_en: 'Rectangle',
          distractors_ar: ['دائرة تامة', 'قطع ناقص', 'شبه منحرف'],
          distractors_en: ['Perfect circle', 'Ellipse', 'Trapezoid']
        },
        {
          name_ar: 'مكعب مصمت',
          name_en: 'Solid cube',
          cut_ar: 'مستوى يقطع ثلاثة أوجه تلتقي عند نفس الزاوية مائلاً',
          cut_en: 'A plane slicing across three faces sharing a single vertex',
          res_ar: 'مثلث متساوي الأضلاع',
          res_en: 'Equilateral triangle',
          distractors_ar: ['مربع متطابق', 'شبه منحرف', 'متوازي أضلاع'],
          distractors_en: ['Square', 'Trapezoid', 'Parallelogram']
        }
      ];

      const s = prng.pick(solids);
      const allChoices = prng.shuffle([
        { ar: s.res_ar, en: s.res_en, isCorrect: true },
        ...s.distractors_ar.map((d, i) => ({ ar: d, en: s.distractors_en[i], isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">المجسم الهندسي: [ ${s.name_ar} ]</div>
          <div class="iq-stimulus-card-highlight">المستوى القاطع: ${s.cut_ar}</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Geometric Solid: [ ${s.name_en} ]</div>
          <div class="iq-stimulus-card-highlight">Cutting Plane: ${s.cut_en}</div>
        </div>
      `;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en,
        value: c.ar
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا تم قطع ${s.name_ar} بواسطة ${s.cut_ar}، فما هو شكل المقطع العرضي ثنائي الأبعاد الناتج عن القطع؟`,
        questionText_en: `If a ${s.name_en} is sliced by ${s.cut_en}, what 2D cross-sectional shape is formed?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `المقطع الناتج عن هذه الهندسة الفراغية هو: ${s.res_ar}.`,
        explanation_en: `The resulting 2D cross section is: ${s.res_en}.`
      };
    }
  },

  // 3. Top-Down Orthographic Projection (Silhouette)
  {
    id: 'spatial_orthographic_top',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // 3x3 footprint shapes
      // Pattern A: L-shape, Pattern B: T-shape, Pattern C: Plus shape, Pattern D: Diagonal
      const patterns = [
        {
          id: 'L_shape',
          grid: [[1,0,0],[1,0,0],[1,1,1]],
          name_ar: 'شكل حرف L بزاوية قائمة',
          name_en: 'L-shape footprint'
        },
        {
          id: 'T_shape',
          grid: [[1,1,1],[0,1,0],[0,1,0]],
          name_ar: 'شكل حرف T متناظر',
          name_en: 'T-shape footprint'
        },
        {
          id: 'Plus_shape',
          grid: [[0,1,0],[1,1,1],[0,1,0]],
          name_ar: 'شكل علامة الجمع (+)',
          name_en: 'Plus sign footprint'
        },
        {
          id: 'U_shape',
          grid: [[1,0,1],[1,0,1],[1,1,1]],
          name_ar: 'شكل حدوة الحصان (U)',
          name_en: 'U-shape footprint'
        }
      ];

      const chosen = prng.pick(patterns);
      const otherPatterns = patterns.filter(p => p.id !== chosen.id);

      const draw2DGrid = (grid, color = '#38bdf8') => (s) => {
        let svg = `<g transform="translate(${s*0.1}, ${s*0.1})">`;
        const cellSize = (s * 0.8) / 3;
        for (let r = 0; r < 3; r++) {
          for (let c = 0; c < 3; c++) {
            const filled = grid[r][c] === 1;
            svg += `<rect x="${c * cellSize}" y="${r * cellSize}" width="${cellSize - 2}" height="${cellSize - 2}" rx="3" fill="${filled ? color : 'rgba(255,255,255,0.06)'}" stroke="rgba(255,255,255,0.1)"/>`;
          }
        }
        svg += `</g>`;
        return svg;
      };

      const allChoices = prng.shuffle([
        { grid: chosen.grid, isCorrect: true },
        ...otherPatterns.map(p => ({ grid: p.grid, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      // Render 3D isometric representation of chosen footprint
      let isoSvg = '';
      const w = 26;
      const h = 26;
      const ox = 190;
      const oy = 110;

      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          if (chosen.grid[r][c] === 1) {
            const z = 0;
            const px = ox + (c - r) * (w * 0.866);
            const py = oy + (c + r) * (w * 0.5) - (z * h);
            const top = `M ${px} ${py - h} L ${px + w*0.866} ${py - h*0.5} L ${px} ${py} L ${px - w*0.866} ${py - h*0.5} Z`;
            const left = `M ${px - w*0.866} ${py - h*0.5} L ${px} ${py} L ${px} ${py + h} L ${px - w*0.866} ${py + h*0.5} Z`;
            const right = `M ${px} ${py} L ${px + w*0.866} ${py - h*0.5} L ${px + w*0.866} ${py + h*0.5} L ${px} ${py + h} Z`;

            isoSvg += `
              <path d="${top}" fill="#38bdf8" stroke="#0f172a" stroke-width="1.2"/>
              <path d="${left}" fill="#0284c7" stroke="#0f172a" stroke-width="1.2"/>
              <path d="${right}" fill="#0369a1" stroke="#0f172a" stroke-width="1.2"/>
            `;
          }
        }
      }

      const stimulusSvg = `<svg viewBox="0 0 380 180" xmlns="http://www.w3.org/2000/svg" style="max-height:170px; margin:0 auto; display:block;">
        <rect width="380" height="180" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        ${isoSvg}
      </svg>`;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(draw2DGrid(c.grid), 110, `Option ${idx + 1}`),
        value: `grid_${idx}`
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي من المخططات ثنائية الأبعاد التالية يمثل المسقط الرأسي العلوي (Top-Down View) للمجسم ثلاثي الأبعاد الموضح؟`,
        questionText_en: `Which 2D grid correctly represents the top-down orthographic plan view of the 3D structure?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `المسقط الرأسي العلوي يظهر فقط مواضع المكعبات كما تبدو من الأعلى تماماً دون تجسيم.`,
        explanation_en: `The top-down orthographic projection depicts the occupied grid coordinates looking directly from above.`
      };
    }
  },

  // 4. Transparent Film Pattern Overlays
  {
    id: 'spatial_film_overlay',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Film 1 has a diagonal slash and vertical line
      // Film 2 has a circle and horizontal line
      // When overlaid, they combine into a single composite symbol
      const shapesA = ['line_h', 'line_v', 'diag_fwd', 'diag_back'];
      const shapesB = ['circle', 'square', 'cross', 'diamond'];

      const chosenA = prng.pick(shapesA);
      const chosenB = prng.pick(shapesB);

      const drawComponent = (shape, s, color = '#38bdf8') => {
        const mid = s / 2;
        if (shape === 'line_h') return `<line x1="${s*0.15}" y1="${mid}" x2="${s*0.85}" y2="${mid}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`;
        if (shape === 'line_v') return `<line x1="${mid}" y1="${s*0.15}" x2="${mid}" y2="${s*0.85}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`;
        if (shape === 'diag_fwd') return `<line x1="${s*0.2}" y1="${s*0.8}" x2="${s*0.8}" y2="${s*0.2}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`;
        if (shape === 'diag_back') return `<line x1="${s*0.2}" y1="${s*0.2}" x2="${s*0.8}" y2="${s*0.8}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`;
        if (shape === 'circle') return `<circle cx="${mid}" cy="${mid}" r="${s*0.3}" fill="none" stroke="${color}" stroke-width="2.5"/>`;
        if (shape === 'square') return `<rect x="${s*0.2}" y="${s*0.2}" width="${s*0.6}" height="${s*0.6}" rx="3" fill="none" stroke="${color}" stroke-width="2.5"/>`;
        if (shape === 'cross') return `<line x1="${s*0.2}" y1="${mid}" x2="${s*0.8}" y2="${mid}" stroke="${color}" stroke-width="2.5"/><line x1="${mid}" y1="${s*0.2}" x2="${mid}" y2="${s*0.8}" stroke="${color}" stroke-width="2.5"/>`;
        return `<polygon points="${mid},${s*0.15} ${s*0.85},${mid} ${mid},${s*0.85} ${s*0.15},${mid}" fill="none" stroke="${color}" stroke-width="2.5"/>`;
      };

      const drawCombined = (sA, sB) => (s) => {
        return drawComponent(sA, s, '#38bdf8') + drawComponent(sB, s, '#ec4899');
      };

      const correctDrawer = drawCombined(chosenA, chosenB);

      // Distractors: wrong A or wrong B
      const otherA = shapesA.filter(x => x !== chosenA);
      const otherB = shapesB.filter(x => x !== chosenB);
      const distractors = [
        drawCombined(otherA[0], chosenB),
        drawCombined(chosenA, otherB[0]),
        drawCombined(otherA[1], otherB[1])
      ];

      const allChoices = prng.shuffle([
        { fn: correctDrawer, isCorrect: true },
        ...distractors.map(d => ({ fn: d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      // Stimulus SVG showing Film 1 + Film 2 = ?
      const stimulusSvg = `<svg viewBox="0 0 380 120" xmlns="http://www.w3.org/2000/svg" style="max-height:120px; margin:0 auto; display:block;">
        <rect width="380" height="120" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <!-- Film 1 -->
        <g transform="translate(60, 20)">
          <rect width="80" height="80" rx="8" fill="rgba(56, 189, 248, 0.08)" stroke="#38bdf8" stroke-width="2" stroke-dasharray="4 3"/>
          ${drawComponent(chosenA, 80, '#38bdf8')}
        </g>
        <text x="165" y="68" fill="#f8fafc" font-size="24" font-weight="700" text-anchor="middle">+</text>
        <!-- Film 2 -->
        <g transform="translate(190, 20)">
          <rect width="80" height="80" rx="8" fill="rgba(236, 72, 153, 0.08)" stroke="#ec4899" stroke-width="2" stroke-dasharray="4 3"/>
          ${drawComponent(chosenB, 80, '#ec4899')}
        </g>
        <text x="295" y="68" fill="#f8fafc" font-size="24" font-weight="700" text-anchor="middle">=</text>
        <text x="335" y="68" fill="#38bdf8" font-size="26" font-weight="700" text-anchor="middle">?</text>
      </svg>`;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(c.fn, 110, `Option ${idx + 1}`),
        value: `opt_${idx}`
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا تم وضع الشريحتين الشفافتين فوق بعضهما تماماً دون تدوير، فما هو الشكل المركب الناتج؟`,
        questionText_en: `If both transparent sheets are overlaid directly without rotation, what composite image is formed?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `التراكب الشفاف يدمج الخطوط والعناصر من الشريحتين معاً في نفس الموضع الهندسي.`,
        explanation_en: `The transparent superposition merges all contours from both sheets without alteration.`
      };
    }
  },

  // 5. Diagonal 45-degree Mirror Reflection
  {
    id: 'spatial_diagonal_reflection',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Shape with distinct asymmetry (e.g. arrow pointing right with a flag on top)
      // Mirror is diagonal line from bottom-left to top-right (y = -x in screen coords)
      // A horizontal pointing arrow reflects to a vertical pointing arrow!
      const dirs = [
        { orig_ar: 'سهم يشير للأعلى مع جناح لليمين', ref_ar: 'سهم يشير لليمين مع جناح للأعلى', ref_en: 'Arrow pointing right with upper wing' },
        { orig_ar: 'سهم يشير لليمين مع جناح للأسفل', ref_ar: 'سهم يشير للأعلى مع جناح لليسار', ref_en: 'Arrow pointing up with left wing' }
      ];
      const d = prng.pick(dirs);

      const drawArrow = (angle) => (s) => {
        const mid = s / 2;
        return `<g transform="translate(${mid}, ${mid}) rotate(${angle}) translate(-${mid}, -${mid})">
          <line x1="${s*0.2}" y1="${mid}" x2="${s*0.75}" y2="${mid}" stroke="#38bdf8" stroke-width="4" stroke-linecap="round"/>
          <polyline points="${s*0.55},${mid - s*0.2} ${s*0.75},${mid} ${s*0.55},${mid + s*0.2}" fill="none" stroke="#38bdf8" stroke-width="4" stroke-linecap="round"/>
          <circle cx="${s*0.4}" cy="${mid - s*0.18}" r="${s*0.08}" fill="#ec4899"/>
        </g>`;
      };

      const correctAngle = 270; // 90 deg counter reflection
      const distractors = [0, 90, 180];

      const allAngles = prng.shuffle([correctAngle, ...distractors]);
      const correctIndex = allAngles.indexOf(correctAngle);

      const makeMirrorSvg = (mirrorText) => `<svg viewBox="0 0 380 140" xmlns="http://www.w3.org/2000/svg" style="max-height:140px; margin:0 auto; display:block;">
        <rect width="380" height="140" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <!-- Original shape in left quadrant -->
        <g transform="translate(60, 20)">
          ${drawArrow(0)(100)}
        </g>
        <!-- 45-degree mirror line -->
        <line x1="170" y1="120" x2="210" y2="20" stroke="#f59e0b" stroke-width="2.5" stroke-dasharray="5 4"/>
        <text x="190" y="132" fill="#f59e0b" font-size="11" font-weight="700" text-anchor="middle">${mirrorText}</text>
        <!-- Question target -->
        <text x="290" y="78" fill="#38bdf8" font-size="28" font-weight="700" text-anchor="middle">?</text>
      </svg>`;

      const stimulusHtml_ar = makeMirrorSvg('خط المرآة 45°');
      const stimulusHtml_en = makeMirrorSvg('Mirror Line 45°');

      const options = allAngles.map((ang, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawArrow(ang), 110, `Option ${idx + 1}`),
        value: `angle_${ang}`
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي من الخيارات يمثل الانعكاس الصحيح للشكل عبر خط المرآة المائل بزاوية 45 درجة؟`,
        questionText_en: `Which choice correctly represents the reflection of the shape across the 45-degree diagonal mirror line?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `الانعكاس عبر خط مائل بزاوية 45 درجة يبدل الإحداثيات الأفقية والرأسية، فتحول الحركة الأفقية إلى حركة رأسية مطابقة.`,
        explanation_en: `Reflection across a 45-degree axis swaps horizontal and vertical vector components.`
      };
    }
  },

  // 6. Cylinder Diagonal Strip Unroll
  {
    id: 'spatial_cylinder_unroll',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // A cylinder with a single helical stripe that goes 360 deg around it from bottom to top
      // When unrolled into a flat rectangle, it forms a single diagonal straight line!
      const correctAns_ar = 'خط مستقيم مائل يربط بين زاويتين متقابلتين';
      const correctAns_en = 'A straight diagonal line connecting opposite corners';

      const distractors = [
        { ar: 'منحنى جيبي موجي (شكل موجة)', en: 'A sinusoidal sine wave curve' },
        { ar: 'خطان رأسيان متوازيان تماماً', en: 'Two vertical parallel lines' },
        { ar: 'قوس دائري نصف قطري مقوس', en: 'A circular curved arc' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const makeUnrollSvg = (t1, t2) => `<svg viewBox="0 0 380 130" xmlns="http://www.w3.org/2000/svg" style="max-height:130px; margin:0 auto; display:block;">
        <rect width="380" height="130" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <!-- Cylinder 3D -->
        <g transform="translate(70, 20)">
          <ellipse cx="40" cy="15" rx="35" ry="10" fill="none" stroke="#38bdf8" stroke-width="2"/>
          <line x1="5" y1="15" x2="5" y2="75" stroke="#38bdf8" stroke-width="2"/>
          <line x1="75" y1="15" x2="75" y2="75" stroke="#38bdf8" stroke-width="2"/>
          <path d="M 5,75 A 35,10 0 0,0 75,75" fill="none" stroke="#38bdf8" stroke-width="2"/>
          <!-- Spiral stripe -->
          <path d="M 5,75 Q 40,45 75,15" fill="none" stroke="#ec4899" stroke-width="3.5"/>
        </g>
        <text x="200" y="60" fill="#f8fafc" font-size="13" font-weight="600">${t1}</text>
        <text x="200" y="80" fill="#94a3b8" font-size="12">${t2}</text>
        <text x="325" y="70" fill="#38bdf8" font-size="24" font-weight="700">?</text>
      </svg>`;

      const stimulusHtml_ar = makeUnrollSvg('فرد السطح الجانبي', 'إلى مستطيل مسطح  →');
      const stimulusHtml_en = makeUnrollSvg('Unroll Lateral Surface', 'Into flat rectangle  →');

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en,
        value: c.ar
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا تم فتح وفرد السطح الجانبي للأسطوانة الموضحة ليصبح مستطيلاً مسطحاً، فما هو شكل الشريط الملتف عليها بدورة كاملة؟`,
        questionText_en: `If the lateral surface of this cylinder is unrolled flat into a rectangle, what shape will the full single-turn helical stripe take?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `الحركة اللولبية المنتظمة بارتفاع ثابت تصبح خطاً مستقيماً مائلاً عند فرد السطح الأسطواني ثنائي الأبعاد.`,
        explanation_en: `A uniform cylindrical helix unrolls into a continuous straight diagonal line connecting opposite corners.`
      };
    }
  },

  // 7. Drilled Cube Missing Unit Blocks
  {
    id: 'spatial_drilled_cube_blocks',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // 3x3x3 solid cube has 27 unit cubes.
      // A tunnel of 1x1 is drilled through the center from top to bottom (removes 3 cubes).
      // Another tunnel is drilled from front to back through the center (removes 3 cubes, but center was already removed => 2 cubes).
      // Total removed = 3 + 2 = 5 cubes. Remaining = 27 - 5 = 22 cubes.
      // If 3 tunnels (X, Y, Z), removed = 3 + 2 + 2 = 7 cubes. Remaining = 20 cubes.
      const modes = [
        { tunnels: 2, removed: 5, remaining: 22, desc_ar: 'نفقان متعامدان يمران بمركز المكعب (من الأعلى للأسفل، ومن الأمام للخلف)', desc_en: 'Two perpendicular tunnels passing through the cube center' },
        { tunnels: 3, removed: 7, remaining: 20, desc_ar: 'ثلاثة أنفاق متعامدة تمر بمركز المكعب عبر المحاور الثلاثة (X, Y, Z)', desc_en: 'Three mutually perpendicular tunnels drilled along X, Y, and Z axes' }
      ];
      const m = prng.pick(modes);
      const correctVal = m.remaining;

      const distSet = new Set();
      distSet.add(27 - (m.tunnels * 3)); // Forgetting intersection
      distSet.add(correctVal + 2);
      distSet.add(correctVal - 2);
      distSet.add(correctVal + 1);
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">مكعب مصمت بحجم 3 × 3 × 3 (يتكون من 27 مكعباً صغيراً)</div>
          <div class="iq-stimulus-card-body">${m.desc_ar}</div>
          <div class="iq-stimulus-card-muted">كل نفق يفرغ عموداً كاملاً من المكعبات الصغيرة (1 × 1 × 3)</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Solid 3 × 3 × 3 Cube (27 unit cubes)</div>
          <div class="iq-stimulus-card-body">${m.desc_en}</div>
          <div class="iq-stimulus-card-muted">Each tunnel removes a full column of unit cubes (1 × 1 × 3)</div>
        </div>
      `;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val} مكعباً`,
        text_en: `${val} cubes`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا حُفرت في مكعب مصمت (3×3×3) ${m.desc_ar}، فكم مكعباً صغيراً يتبقى من أصل 27؟`,
        questionText_en: `If in a solid 3x3x3 cube, ${m.desc_en}, how many small cubes remain out of 27?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `المكعب الأصلي = 27. النفق الأول يزيل 3 مكعبات، والأنفاق الإضافية تتقاطع في المركز المشترك، مما يزيل إجمالاً ${m.removed} مكعباً. الباقي = 27 - ${m.removed} = ${correctVal}.`,
        explanation_en: `Original total = 27. Accounting for the shared central intersection cube, total removed is ${m.removed}, leaving ${correctVal} cubes.`
      };
    }
  },

  // 8. 3D Dual-Axis Sequential Rotation
  {
    id: 'spatial_dual_axis_rotation',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // 3D arrow: pointing North, colored dot on East side
      // Step 1: Rotate 90 deg clockwise around Z axis (points East, dot on South)
      // Step 2: Pitch 90 deg forward around X axis
      const result_ar = 'يشير للأمام نحو المشاهد وتستقر النقطة في الأسفل';
      const result_en = 'Points toward viewer with dot resting at bottom';

      const distractors = [
        { ar: 'يشير للأعلى وتستقر النقطة في الجهة اليمنى', en: 'Points up with dot on right' },
        { ar: 'يشير للخلف بعيداً عن المشاهد والنقطة بالأعلى', en: 'Points away from viewer with dot on top' },
        { ar: 'يشير لليسار وتستقر النقطة في الجهة الأمامية', en: 'Points left with dot facing front' }
      ];

      const allChoices = prng.shuffle([
        { ar: result_ar, en: result_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">المجسم: سهم رأسي يشير للأعلى مع علامة نقطية على جانبه الأيمن</div>
          <div class="iq-stimulus-card-body">الخطوة 1: تدوير 90° لليمين مع عقارب الساعة حول المحور الرأسي</div>
          <div class="iq-stimulus-card-highlight">الخطوة 2: إمالة 90° للأمام باتجاه المشاهد حول المحور الأفقي</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Object: Vertical arrow pointing UP with a dot on its right side</div>
          <div class="iq-stimulus-card-body">Step 1: Rotate 90° clockwise around the vertical axis</div>
          <div class="iq-stimulus-card-highlight">Step 2: Pitch 90° forward toward the viewer around the horizontal axis</div>
        </div>
      `;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en,
        value: c.ar
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا خضع المجسم للتدويرين المتتاليين الموضحين في الفضاء ثلاثي الأبعاد، فما هو اتجاه السهم وموضع العلامة النقطية في النهاية؟`,
        questionText_en: `Following the two sequential 3D rotations, what will be the arrow's orientation and dot position?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `بعد الخطوة 1: يتجه السهم لليمين والنقطة للخلف/الأسفل. بعد الخطوة 2: يميل السهم ليصبح موجهاً للأمام نحو المشاهد والنقطة في الأسفل.`,
        explanation_en: `After step 1, arrow points right. Pitching forward orientates the arrow directly toward the viewer with dot on the bottom.`
      };
    }
  },

  // 9. Paper Hole Punch Symmetrical Unfolding
  {
    id: 'spatial_hole_punch_unfold',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Square sheet folded in half vertically, then folded in half horizontally (into a 1/4 size square)
      // A hole is punched at the single open outer corner vs folded center corner
      const punchModes = [
        {
          atCenter: true,
          holesCount: 1,
          desc_ar: 'ثقب دائري في الزاوية المغلقة المطوية (التي تمثل مركز الورقة الأصلي)',
          desc_en: 'Punched at the folded center corner',
          res_ar: 'ثقب واحد كبير في مركز الورقة المفتوحة تماماً',
          res_en: 'One single hole at the exact center'
        },
        {
          atCenter: false,
          holesCount: 4,
          desc_ar: 'ثقب دائري في الزاوية الخارجية المفتوحة تماماً للورقة المطوية',
          desc_en: 'Punched at the outer open corner',
          res_ar: 'أربعة ثقوب متطابقة في زوايا الورقة الأربع',
          res_en: 'Four holes at the four outer corners'
        }
      ];

      const m = prng.pick(punchModes);
      const distractors = [
        { ar: 'ثقبان فقط في منتصف الحافتين العلوية والسفلية', en: 'Two holes on top and bottom edges' },
        { ar: 'ثقبان متقابلان قطرياً في زاويتين متضادتين', en: 'Two diagonally opposite holes' },
        { ar: m.holesCount === 1 ? 'أربعة ثقوب في الزوايا الأربع' : 'ثقب واحد في مركز الورقة', en: m.holesCount === 1 ? 'Four holes at corners' : 'One hole in center' }
      ];

      const allChoices = prng.shuffle([
        { ar: m.res_ar, en: m.res_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">طي ورقة مربعة مرتين: رأسياً ثم أفقياً إلى ربع حجمها</div>
          <div class="iq-stimulus-card-highlight">${m.desc_ar}</div>
          <div class="iq-stimulus-card-muted">ماذا نرى عند إعادة فرد الورقة بالكامل؟</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Square sheet folded twice: vertically then horizontally into quarters</div>
          <div class="iq-stimulus-card-highlight">${m.desc_en}</div>
          <div class="iq-stimulus-card-muted">What pattern emerges when completely unfolded?</div>
        </div>
      `;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en,
        value: c.ar
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا طويت ورقة مربعة مرتين إلى الربع، ثم تم عمل ${m.desc_ar}، فكيف تبدو الثقوب عند فرد الورقة؟`,
        questionText_en: `If a square sheet is folded twice into quarters and ${m.desc_en}, how will the holes appear unfolded?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `الزاوية المختارة تحدد موضع ومضاعفة الثقوب: ${m.res_ar}.`,
        explanation_en: `The location of the punch relative to the fold folds determines symmetry: ${m.res_en}.`
      };
    }
  },

  // 10. Multi-Angle Observer Viewpoint
  {
    id: 'spatial_observer_viewpoint',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Table with 3 colored cones: Red at North, Green at East, Blue at South
      // Observer stands at East looking West:
      // What is the order of cones from left to right for the observer?
      // Left is South (Blue), Center is between them, Right is North (Red)
      const correctAns_ar = 'الأزرق على اليسار، والأحمر على اليمين';
      const correctAns_en = 'Blue on the left, Red on the right';

      const distractors = [
        { ar: 'الأحمر على اليسار، والأزرق على اليمين', en: 'Red on the left, Blue on the right' },
        { ar: 'الأخضر في المنتصف، والأحمر على اليسار', en: 'Green in center, Red on left' },
        { ar: 'الأزرق في المركز محجوباً بالكامل بالأحمر', en: 'Blue in center completely occluded' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const makeObserverSvg = (redLabel, blueLabel, obsLabel) => `<svg viewBox="0 0 380 120" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="120" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <!-- Compass points -->
        <circle cx="120" cy="65" r="45" fill="none" stroke="rgba(255,255,255,0.15)" stroke-width="1.5"/>
        <circle cx="120" cy="30" r="7" fill="#ef4444"/>
        <text x="120" y="20" fill="#ef4444" font-size="11" font-weight="700" text-anchor="middle">${redLabel}</text>
        <circle cx="120" cy="100" r="7" fill="#38bdf8"/>
        <text x="120" y="116" fill="#38bdf8" font-size="11" font-weight="700" text-anchor="middle">${blueLabel}</text>
        <!-- Observer at East -->
        <g transform="translate(225, 65)">
          <circle cx="0" cy="0" r="10" fill="#f59e0b"/>
          <path d="M-12,-4 L-22,0 L-12,4" fill="none" stroke="#f59e0b" stroke-width="2"/>
          <text x="15" y="4" fill="#f59e0b" font-size="11" font-weight="700">${obsLabel}</text>
        </g>
      </svg>`;

      const stimulusHtml_ar = makeObserverSvg('أحمر (شمال)', 'أزرق (جنوب)', 'مراقب ينظر غرباً ←');
      const stimulusHtml_en = makeObserverSvg('Red (North)', 'Blue (South)', 'Looking West ←');

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en,
        value: c.ar
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا وقف مراقب في جهة الشرق ناظراً باتجاه الغرب نحو مركز الطاولة، فكيف يرى ترتيب النقطتين (الأحمر والأزرق) من يساره إلى يمينه؟`,
        questionText_en: `If an observer stands at the East looking West toward the center, how will the Red and Blue markers appear from left to right?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `عند النظر باتجاه الغرب: الجنوب يقع على يسار المشاهد (النقطة الزرقاء)، والشمال يقع على يمينه (النقطة الحمراء).`,
        explanation_en: `Looking West, South is on the observer's left (Blue) and North is on their right (Red).`
      };
    }
  },

  // 11. Polygon Tiling Complementary Notch
  {
    id: 'spatial_polygon_tile_notch',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Hexagon with a triangular bite missing from one side
      // The complement is an identical triangle
      const shapes = [
        {
          name_ar: 'سداسي منتظم مفقود منه مثلث متساوي الأضلاع على أحد أضلاعه',
          name_en: 'Regular hexagon missing an equilateral triangle on one edge',
          complement_ar: 'مثلث متساوي الأضلاع مطابق للفجوة تماماً',
          complement_en: 'Matching equilateral triangle',
          distractors_ar: ['مربع قائم الزاوية', 'شبه منحرف متساوي الساقين', 'دائرة مقوسة'],
          distractors_en: ['Right-angled square', 'Isosceles trapezoid', 'Curved circle']
        }
      ];
      const s = prng.pick(shapes);

      const allChoices = prng.shuffle([
        { ar: s.complement_ar, en: s.complement_en, isCorrect: true },
        ...s.distractors_ar.map((d, i) => ({ ar: d, en: s.distractors_en[i], isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">${s.name_ar}</div>
          <div class="iq-stimulus-card-body">أي قطعة هندسية تلتحم بالفجوة لإعادة الشكل السداسي مكتملاً دون فراغات؟</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">${s.name_en}</div>
          <div class="iq-stimulus-card-body">Which complementary geometric piece fits into the notch to restore the regular hexagon?</div>
        </div>
      `;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en,
        value: c.ar
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هي القطعة الهندسية المتممة التي تلتحم بالفجوة لإعادة الشكل إلى سداسي منتظم مكتمل؟`,
        questionText_en: `Which complementary geometric piece fits into the notch to restore the complete regular hexagon?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `الفجوة المثلثية تتطلب قطعة مطابقة في الزوايا والأبعاد: ${s.complement_ar}.`,
        explanation_en: `The notch geometry requires an exact matching piece: ${s.complement_en}.`
      };
    }
  },

  // 12. 3D Polyhedron Vertex/Edge Euler Relation
  {
    id: 'spatial_euler_polyhedron',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Euler's Formula: V - E + F = 2 => E = V + F - 2
      const polyhedra = [
        { name_ar: 'ثماني الأوجه المنتظم (Octahedron)', name_en: 'Regular Octahedron', v: 6, f: 8, e: 12 },
        { name_ar: 'عشاري الأوجه الموشوري (Pentagonal Prism)', name_en: 'Pentagonal Prism', v: 10, f: 7, e: 15 },
        { name_ar: 'الهرم السداسي (Hexagonal Pyramid)', name_en: 'Hexagonal Pyramid', v: 7, f: 7, e: 12 },
        { name_ar: 'مكعب مقطوع الرؤوس الجزئي (Dodecahedron)', name_en: 'Regular Dodecahedron', v: 20, f: 12, e: 30 }
      ];

      const p = prng.pick(polyhedra);
      const correctVal = p.e;

      const distSet = new Set();
      distSet.add(p.v + p.f);
      distSet.add(correctVal + 2);
      distSet.add(correctVal - 2);
      distSet.add(correctVal + 4);
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">مجسم متعدد السطوح: [ ${p.name_ar} ]</div>
          <div class="iq-stimulus-card-body">عدد الرؤوس (V) = ${p.v}  •  عدد الأوجه (F) = ${p.f}</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Polyhedron: [ ${p.name_en} ]</div>
          <div class="iq-stimulus-card-body">Vertices (V) = ${p.v}  •  Faces (F) = ${p.f}</div>
        </div>
      `;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val} حافة`,
        text_en: `${val} edges`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا كان مجسم ${p.name_ar} يحتوي على ${p.v} رؤوس و ${p.f} أوجه، فكم عدد حواف (أضلاع) هذا المجسم وفق نظرية أويلر للمجسمات؟`,
        questionText_en: `If a ${p.name_en} has ${p.v} vertices and ${p.f} faces, how many edges does it possess according to Euler's formula?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `صيغة أويلر: الرؤوس (V) - الحواف (E) + الأوجه (F) = 2. إذن الحواف E = ${p.v} + ${p.f} - 2 = ${correctVal} حافة.`,
        explanation_en: `Euler's formula: V - E + F = 2 -> E = ${p.v} + ${p.f} - 2 = ${correctVal} edges.`
      };
    }
  },

  // 13. Rotational Invariance Degree
  {
    id: 'spatial_rotational_symmetry_order',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 30,
    generate(prng, difficulty = 2) {
      const shapes = [
        { name_ar: 'نجمة خماسية منتظمة', name_en: 'Regular 5-point star', order: 5, angle: 72 },
        { name_ar: 'سداسي أضلاع منتظم', name_en: 'Regular hexagon', order: 6, angle: 60 },
        { name_ar: 'مثلث متساوي الأضلاع', name_en: 'Equilateral triangle', order: 3, angle: 120 },
        { name_ar: 'ثماني أضلاع منتظم', name_en: 'Regular octagon', order: 8, angle: 45 }
      ];

      const s = prng.pick(shapes);
      const correctVal = s.angle;

      const distSet = new Set();
      distSet.add(s.angle + 15);
      distSet.add(s.angle * 2);
      distSet.add(Math.max(30, s.angle - 15));
      distSet.add(90);
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">الشكل: [ ${s.name_ar} ]</div>
          <div class="iq-stimulus-card-body">ما هي أصغر زاوية دوران موجبة يعود بها الشكل ليتطابق مع نفسه تماماً؟</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Shape: [ ${s.name_en} ]</div>
          <div class="iq-stimulus-card-body">What is the smallest positive angle of rotation that maps the shape onto itself?</div>
        </div>
      `;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val}°`,
        text_en: `${val}°`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هي أصغر زاوية دوران حول المركز بالدرجات تجعل مجسم (${s.name_ar}) ينطبق على موضعه الأصلي تماماً؟`,
        questionText_en: `What is the smallest positive angle of rotation about its center that maps a (${s.name_en}) onto itself?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `رتبة التماثل الدوراني = ${s.order}. أصغر زاوية تطابق = 360° ÷ ${s.order} = ${correctVal}°.`,
        explanation_en: `Rotational symmetry order = ${s.order}. Smallest angle of congruence = 360° / ${s.order} = ${correctVal}°.`
      };
    }
  },

  // 14. 3D Wireframe Inversion Perspective
  {
    id: 'spatial_wireframe_inversion',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const correctAns_ar = 'المكعب يمكن رؤيته من منظورين متعاكسين (نحو الأعلى أو نحو الأسفل)';
      const correctAns_en = 'The cube can be perceived from two opposing vantage points (Necker cube)';

      const distractors = [
        { ar: 'الشكل مستحيل هندسياً ولا يمكن رسمه', en: 'Geometrically impossible' },
        { ar: 'يحتوي على وجه واحد فقط مفتوح', en: 'Contains only one open face' },
        { ar: 'ينهار المنظور عند تدويره 180 درجة', en: 'Collapses when rotated 180 degrees' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusSvg = `<svg viewBox="0 0 160 140" xmlns="http://www.w3.org/2000/svg" style="max-height:130px; margin:0 auto; display:block;">
        <rect x="20" y="20" width="70" height="70" fill="none" stroke="#38bdf8" stroke-width="2.5"/>
        <rect x="55" y="50" width="70" height="70" fill="none" stroke="#ec4899" stroke-width="2.5"/>
        <line x1="20" y1="20" x2="55" y2="50" stroke="#94a3b8" stroke-width="2"/>
        <line x1="90" y1="20" x2="125" y2="50" stroke="#94a3b8" stroke-width="2"/>
        <line x1="20" y1="90" x2="55" y2="120" stroke="#94a3b8" stroke-width="2"/>
        <line x1="90" y1="90" x2="125" y2="120" stroke="#94a3b8" stroke-width="2"/>
      </svg>`;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en,
        value: c.ar
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هي الخاصية الإدراكية البصرية الأساسية التي يتميز بها مكعب نيكر (Necker Cube) السلكي الموضح؟`,
        questionText_en: `What is the fundamental visual perceptual property exhibited by this wireframe Necker Cube?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `مكعب نيكر هو شكل ثنائي الاستقرار الإدراكي؛ يتبادل الدماغ إدراكه بين رؤية الوجه الأزرق في المقدمة أو الوجه الوردي في المقدمة.`,
        explanation_en: `The Necker cube is a bistable optical illusion where depth perception shifts between two valid 3D interpretations.`
      };
    }
  },

  // 15. Gear Belt Drive Direction
  {
    id: 'spatial_belt_drive_direction',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Pulley A and Pulley B connected by a crossed belt (8-shape)
      // Crossed belt reverses rotational direction!
      // Open belt preserves rotational direction!
      const isCrossed = prng.pick([true, false]);
      const dirA_ar = 'مع عقارب الساعة';
      const dirA_en = 'Clockwise';

      const correctAns_ar = isCrossed ? 'عكس عقارب الساعة' : 'مع عقارب الساعة';
      const correctAns_en = isCrossed ? 'Counter-clockwise' : 'Clockwise';

      const wrongAns_ar = isCrossed ? 'مع عقارب الساعة' : 'عكس عقارب الساعة';
      const wrongAns_en = isCrossed ? 'Clockwise' : 'Counter-clockwise';

      const distractors = [
        { ar: wrongAns_ar, en: wrongAns_en },
        { ar: 'تتذبذب للأمام والخلف دون استمرار', en: 'Oscillates back and forth' },
        { ar: 'تتوقف عن الدوران بسبب الاحتكاك', en: 'Halts due to friction' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const makePulleySvg = (labelA) => `<svg viewBox="0 0 380 110" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="110" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <!-- Pulley A -->
        <circle cx="90" cy="55" r="30" fill="none" stroke="#38bdf8" stroke-width="3"/>
        <text x="90" y="60" fill="#38bdf8" font-size="12" font-weight="700" text-anchor="middle">${labelA}</text>
        <!-- Pulley B -->
        <circle cx="270" cy="55" r="30" fill="none" stroke="#ec4899" stroke-width="3"/>
        <text x="270" y="60" fill="#ec4899" font-size="12" font-weight="700" text-anchor="middle">B (?)</text>
        <!-- Belt -->
        ${isCrossed
          ? '<line x1="90" y1="25" x2="270" y2="85" stroke="#f59e0b" stroke-width="2.5"/><line x1="90" y1="85" x2="270" y2="25" stroke="#f59e0b" stroke-width="2.5"/>'
          : '<line x1="90" y1="25" x2="270" y2="25" stroke="#f59e0b" stroke-width="2.5"/><line x1="90" y1="85" x2="270" y2="85" stroke="#f59e0b" stroke-width="2.5"/>'
        }
      </svg>`;

      const stimulusHtml_ar = makePulleySvg('A (مع)');
      const stimulusHtml_en = makePulleySvg('A (CW)');

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا كانت البكرة (A) تدور مع عقارب الساعة وموصولة بالبكرة (B) عبر سير ناقل ${isCrossed ? 'متقاطع (على شكل رقم 8)' : 'مستقيم ومفتوح'}، ففي أي اتجاه تدور البكرة (B)؟`,
        questionText_en: `If Pulley A rotates clockwise connected to Pulley B via a ${isCrossed ? 'crossed belt' : 'open straight belt'}, in which direction does Pulley B rotate?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: isCrossed
          ? `السير المتقاطع يعكس اتجاه الحركة الدورانية بين البكرتين، لذا تدور البكرة B عكس عقارب الساعة.`
          : `السير المفتوح المستقيم ينقل الحركة بنفس الاتجاه الأصلي، لذا تدور البكرة B مع عقارب الساعة.`,
        explanation_en: isCrossed
          ? `A crossed belt reverses the rotational direction between pulleys.`
          : `An open belt maintains identical rotational direction.`
      };
    }
  }
];
